import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { usePaged, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Pencil, Trash2, Search, Upload, Download, ImagePlus, User, GraduationCap, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", nik: "", gender: "L", birth_place: "", birth_date: "",
  last_education: "", phone: "", pin: "", jabatan: "", category: "pendidik", address: "" };

export default function DataPendidik() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [fCat, setFCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [photoFor, setPhotoFor] = useState(null);
  const fileRef = useRef(null);
  const importRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/educators").then((r) => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...EMPTY, ...e }); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim() || !form.pin.trim()) return toast.error("Nama & PIN wajib diisi");
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/educators/${editing.id}`, form);
      else await api.post("/admin/educators", form);
      toast.success(editing ? "Data diperbarui" : "Pendidik ditambahkan");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (e) => {
    if (!window.confirm(`Hapus data ${e.name}?`)) return;
    try { await api.delete(`/admin/educators/${e.id}`); toast.success("Dihapus"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadPhoto = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file || !photoFor) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/educators/${photoFor.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto diperbarui"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPhotoFor(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/admin/educators/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "template_import_pendidik.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh template"); }
  };

  const doImport = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/educators/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.created} pendidik diimpor`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} baris dilewati`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ""; }
  };

  const filtered = list.filter((e) =>
    (fCat === "all" || e.category === fCat) &&
    (e.name.toLowerCase().includes(query.toLowerCase()) || (e.nik || "").includes(query) || (e.pin || "").includes(query))
  );
  const pg = usePaged(filtered, 10);
  const catLabel = (c) => (c === "kependidikan" ? "Kependidikan" : "Pendidik");

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>;

  return (
    <div>
      <PageHeader
        title="Pendidik & Kependidikan"
        subtitle="Data guru & staf. Mereka berbelanja di kopontren tanpa saldo (dibayar saat gajian) dan bebas lintas unit."
        action={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadTemplate} data-testid="pendidik-template" className="rounded-xl"><Download size={16} className="mr-1.5" /> Template</Button>
            <Button variant="outline" onClick={() => importRef.current?.click()} data-testid="pendidik-import" disabled={importing} className="rounded-xl">
              {importing ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Upload size={16} className="mr-1.5" />} Impor
            </Button>
            <Button onClick={openNew} data-testid="add-pendidik-btn" className="rounded-xl bg-emerald-600 hover:bg-emerald-700"><UserPlus size={16} className="mr-1.5" /> Tambah</Button>
          </div>
        }
      />
      <input ref={importRef} type="file" accept=".xlsx" className="hidden" onChange={doImport} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />

      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={fCat} onValueChange={setFCat}>
          <SelectTrigger className="w-44 rounded-xl bg-white" data-testid="pendidik-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            <SelectItem value="pendidik">Pendidik</SelectItem>
            <SelectItem value="kependidikan">Kependidikan</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="pendidik-search" placeholder="Cari nama / NIK / PIN…" className="pl-10 rounded-xl bg-white" />
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {list.length === 0 ? (
          <Empty><GraduationCap className="mx-auto mb-2" /> Belum ada data pendidik/kependidikan.</Empty>
        ) : filtered.length === 0 ? (
          <Empty><Search className="mx-auto mb-2" /> Tidak ada hasil.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Foto</TableHead><TableHead>Nama</TableHead><TableHead>NIK</TableHead>
                <TableHead>Kategori</TableHead><TableHead>Jabatan</TableHead><TableHead>PIN</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((e) => (
                <TableRow key={e.id} data-testid={`pendidik-row-${e.id}`}>
                  <TableCell>
                    <button onClick={() => { setPhotoFor(e); fileRef.current?.click(); }} data-testid={`pendidik-photo-${e.id}`} className="w-10 h-10 rounded-full overflow-hidden bg-stone-100 flex items-center justify-center border border-stone-200 hover:ring-2 ring-emerald-400" title="Ubah foto">
                      {e.photo_path ? <img src={fileUrl(e.photo_path)} alt="" className="w-full h-full object-cover" /> : <User size={18} className="text-stone-400" />}
                    </button>
                  </TableCell>
                  <TableCell className="font-medium">{e.name}<span className="block text-xs text-stone-400">{e.gender === "P" ? "Perempuan" : "Laki-laki"}</span></TableCell>
                  <TableCell className="text-sm text-stone-600">{e.nik || "-"}</TableCell>
                  <TableCell><span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${e.category === "kependidikan" ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700"}`}>{catLabel(e.category)}</span></TableCell>
                  <TableCell className="text-sm text-stone-600">{e.jabatan || "-"}</TableCell>
                  <TableCell className="font-mono">{e.pin}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-pendidik-${e.id}`}><Pencil size={15} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(e)} data-testid={`del-pendidik-${e.id}`} className="text-rose-600"><Trash2 size={15} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager {...pg} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Data" : "Tambah Pendidik/Kependidikan"}</DialogTitle>
            <DialogDescription>Lengkapi data. PIN dipakai kasir untuk mengidentifikasi saat berbelanja.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nama" v={form.name} on={(x) => setForm({ ...form, name: x })} testid="f-name" />
            <Field label="NIK" v={form.nik} on={(x) => setForm({ ...form, nik: x })} testid="f-nik" />
            <div>
              <Label>Jenis Kelamin</Label>
              <Select value={form.gender} onValueChange={(x) => setForm({ ...form, gender: x })}>
                <SelectTrigger className="rounded-xl" data-testid="f-gender"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="L">Laki-laki</SelectItem><SelectItem value="P">Perempuan</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(x) => setForm({ ...form, category: x })}>
                <SelectTrigger className="rounded-xl" data-testid="f-category"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pendidik">Pendidik</SelectItem><SelectItem value="kependidikan">Kependidikan</SelectItem></SelectContent>
              </Select>
            </div>
            <Field label="Tempat Lahir" v={form.birth_place} on={(x) => setForm({ ...form, birth_place: x })} testid="f-bplace" />
            <div>
              <Label>Tanggal Lahir</Label>
              <Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="f-bdate" className="rounded-xl" />
            </div>
            <Field label="Pendidikan Terakhir" v={form.last_education} on={(x) => setForm({ ...form, last_education: x })} testid="f-edu" />
            <Field label="No HP" v={form.phone} on={(x) => setForm({ ...form, phone: x })} testid="f-phone" />
            <Field label="PIN" v={form.pin} on={(x) => setForm({ ...form, pin: x })} testid="f-pin" />
            <Field label="Jabatan" v={form.jabatan} on={(x) => setForm({ ...form, jabatan: x })} testid="f-jabatan" />
            <div className="sm:col-span-2">
              <Label>Alamat</Label>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="f-address" className="rounded-xl" />
            </div>
          </div>
          {editing && <p className="text-xs text-stone-400 mt-1 flex items-center gap-1"><ImagePlus size={13} /> Foto dapat diubah lewat ikon foto di tabel setelah data tersimpan.</p>}
          <Button onClick={submit} disabled={saving} data-testid="save-pendidik" className="mt-3 w-full py-6 rounded-xl bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="animate-spin" /> : "Simpan"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, v, on, testid }) => (
  <div>
    <Label>{label}</Label>
    <Input value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testid} className="rounded-xl" />
  </div>
);
